import {
  Package,
  Building2,
  Monitor,
  Settings,
  Search,
  Info,
  CreditCard,
} from 'lucide-react';
import type { Country, Level2PlatformData, Tab } from '../../types';
import type { Product, OnboardingSystem, ColumnGroups } from '../../pages/CatalogPage';
import type { OnboardingCountry } from '../../hooks/useModalState';
import { sortByAvailability, getVisibleCountries } from '../../utils/tableHelpers';
import { HardwareTableSection } from '../tables/HardwareTableSection';
import { SoftwareTableSection } from '../tables/SoftwareTableSection';
import { Level2PlatformTableSection } from '../tables/Level2PlatformTableSection';
import { PartnerIntegrationsTable } from '../tables/PartnerIntegrationsTable';
import { DinePaymentsTable } from '../tables/DinePaymentsTable';
import { SearchResultsSection } from './SearchResultsSection';
import { CountryColumnHeaders } from './CountryColumnHeaders';
import { EmptyState } from './EmptyState';
import { ViewHint } from '../tables/ViewHint';

type SharedTableProps = {
  browseMode: boolean;
  selectedCountry: string;
  COUNTRIES: Country[];
  unifiedVisibleCountries: Country[];
  highlightedProduct: string | null;
};

type TabContentProps = SharedTableProps & {
  activeTab: string;
  tabSearchQuery: string;
  selectedPlatform: string;
  tabs: Tab[];
  searchVisibleCountries: Country[];
  useCompactMode: boolean;
  hardwareProducts: Product[];
  softwareProducts: Product[];
  lbm2Products: Product[];
  level2Platforms: Record<string, Level2PlatformData>;
  level2SearchResults: Record<string, Level2PlatformData>;
  platformProducts: Product[];
  onboardingSystems: OnboardingSystem[];
  onboardingColumnGroups: ColumnGroups | null;
  getCountryFlag: (countryName: string) => string;
  onDeviceClick: (device: Product) => void;
  onProductClick: (product: Product) => void;
  onPartnerClick: (product: Product) => void;
  onOnboardingClick: (data: OnboardingCountry) => void;
};

// ─── Supported Partners (Shift4 Duo) ────────────────────────────────────────

function SupportedPartnersTab({
  platformProducts,
  browseMode,
  selectedCountry,
  COUNTRIES,
  unifiedVisibleCountries,
  onPartnerClick,
}: {
  platformProducts: Product[];
  browseMode: boolean;
  selectedCountry: string;
  COUNTRIES: Country[];
  unifiedVisibleCountries: Country[];
  onPartnerClick: (product: Product) => void;
}) {
  const withPatt = platformProducts.filter(p => p.patt && p.patt.trim() !== '');

  if (withPatt.length === 0) {
    return <EmptyState icon={Package} message="No supported partners with Pay at the Table" />;
  }

  const filtered = withPatt.filter(product =>
    browseMode
      ? unifiedVisibleCountries.some(c => product.countries[c.code] === true)
      : selectedCountry && product.countries[selectedCountry] === true
  );
  const sorted = sortByAvailability(filtered);
  const colWidth = unifiedVisibleCountries.length >= 14 ? 'w-[45px]' : 'w-[60px]';

  return (
    <div className="bg-card border border-border rounded-lg overflow-x-auto">
      <table className="w-full min-w-[600px] table-fixed">
        <thead className="bg-transparent">
          <tr className="h-16">
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[180px]">Partner</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[180px]">Solution</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[120px]">Hardware Family</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[100px]">Integration Type</th>
            <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[80px]">Version</th>
            <th className="text-center px-4 py-2 text-sm font-medium text-foreground bg-transparent w-[100px]">
              <div className="flex flex-col items-center gap-0.5">
                <span>Shift4 Duo</span>
                <span className="text-xs text-muted-foreground font-normal">(Pay / Order at the Table)</span>
              </div>
            </th>
            <CountryColumnHeaders
              browseMode={browseMode}
              visibleCountries={unifiedVisibleCountries}
              selectedCountry={selectedCountry}
              COUNTRIES={COUNTRIES}
            />
          </tr>
        </thead>
        <tbody>
          {sorted.map((product, idx) => {
            const availabilityCell = (value: boolean | string | undefined) => {
              if (value === true) return <span className="text-sm text-green-600">✓</span>;
              if (value === 'N/A') return <span className="text-muted-foreground text-xs">—</span>;
              if (value === 'Coming Soon') return <span className="text-transparent">-</span>;
              return <span className="text-sm text-red-500">✗</span>;
            };

            return (
              <tr
                key={idx}
                className="border-b border-border/50 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-sm cursor-pointer group"
                onClick={() => onPartnerClick(product)}
                title="Click to view integration details"
              >
                <td className="px-4 py-3 text-sm font-medium truncate w-[180px]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-foreground text-sm leading-tight truncate">
                        {product.solution}
                      </div>
                      {product.notes && (
                        <div title="Additional details available">
                          <Info className="w-3 h-3 text-blue-500 flex-shrink-0" />
                        </div>
                      )}
                    </div>
                    <ViewHint />
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground truncate w-[180px]">{product.solution_description || '-'}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground truncate w-[120px]">{product.hardware_family || '-'}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground truncate w-[100px]">{product.model || '-'}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground truncate w-[80px]">{product.tag || '-'}</td>
                <td className="px-4 py-3 text-sm text-center w-[100px]">
                  {product.patt?.trim() ? (
                    <span className="text-muted-foreground">{product.patt}</span>
                  ) : (
                    <span className="text-transparent">-</span>
                  )}
                </td>
                {browseMode
                  ? unifiedVisibleCountries.map(c => (
                      <td key={c.code} className={`text-center px-1 py-2 ${colWidth}`}>
                        {availabilityCell(product.countries?.[c.code])}
                      </td>
                    ))
                  : selectedCountry && (
                      <td className={`text-center px-1 py-2 ${colWidth}`}>
                        {availabilityCell(product.countries?.[selectedCountry])}
                      </td>
                    )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Integrated Payments (vertical- tabs) ────────────────────────────────────

const VERTICAL_NORMALIZERS: { matches: (v: string) => boolean; normalize: (v: string) => string }[] = [
  { matches: v => v === 'F&B', normalize: () => 'Food & Beverage' },
  { matches: v => v === 'Auto Rental', normalize: () => 'Auto' },
  { matches: v => v.startsWith('Ecommerce'), normalize: () => 'Ecommerce' },
  { matches: v => v === 'Moto', normalize: () => 'MOTO' },
  { matches: v => v.includes('Vending'), normalize: v => v.split('(')[0].trim() },
];

function normalizeVertical(v: string): string {
  const rule = VERTICAL_NORMALIZERS.find(r => r.matches(v));
  return rule ? rule.normalize(v) : v;
}

function IntegratedPaymentsVerticalTab({
  activeTab,
  tabSearchQuery,
  platformProducts,
  unifiedVisibleCountries,
  browseMode,
  selectedCountry,
  highlightedProduct,
  onPartnerClick,
}: {
  activeTab: string;
  tabSearchQuery: string;
  platformProducts: Product[];
  unifiedVisibleCountries: Country[];
  browseMode: boolean;
  selectedCountry: string;
  highlightedProduct: string | null;
  onPartnerClick: (product: Product) => void;
}) {
  const isvProducts = platformProducts.filter(p => p.category === 'ISV Partner Integration');

  if (tabSearchQuery.trim()) {
    const query = tabSearchQuery.toLowerCase();
    const matched = isvProducts
      .filter(p =>
        [p.solution, p.solution_description, p.tag, p.vertical, p.notes]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query)
      )
      .filter(p => p.development === 'Ready')
      .filter((p, i, self) => i === self.findIndex(q => q.solution === p.solution && q.vertical === p.vertical));

    if (matched.length === 0) {
      return (
        <EmptyState
          icon={Search}
          message={`No results found for "${tabSearchQuery}"`}
        />
      );
    }

    const grouped = matched.reduce((acc, p) => {
      const key = normalizeVertical(p.vertical);
      if (!acc[key]) acc[key] = [];
      acc[key].push(p);
      return acc;
    }, {} as Record<string, Product[]>);

    return (
      <div className="space-y-6">
        {Object.entries(grouped).map(([verticalName, products]) => (
          <div key={verticalName}>
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {verticalName} Results ({products.length})
            </h3>
            <PartnerIntegrationsTable
              products={products}
              visibleCountries={unifiedVisibleCountries}
              browseMode={browseMode}
              selectedCountry={selectedCountry}
              highlightedProduct={highlightedProduct}
              onPartnerClick={onPartnerClick}
            />
          </div>
        ))}
      </div>
    );
  }

  const verticalTabId = activeTab.replace('vertical-', '');

  let verticalProducts: Product[];
  if (verticalTabId === 'all') {
    verticalProducts = isvProducts;
  } else if (verticalTabId === 'patt') {
    verticalProducts = isvProducts.filter(p => p.patt && p.patt.trim() !== '');
  } else {
    verticalProducts = isvProducts.filter(p => {
      if (verticalTabId === 'auto') {
        return p.vertical === 'Auto' || p.vertical === 'Auto Rental';
      }
      const normalized = normalizeVertical(p.vertical)
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace('&', 'and');
      return normalized === verticalTabId;
    });
  }

  verticalProducts = verticalProducts.filter(p => p.development === 'Ready');

  if (verticalProducts.length === 0) {
    return <EmptyState icon={Building2} message="No products available for this vertical" />;
  }

  return (
    <PartnerIntegrationsTable
      products={verticalProducts}
      visibleCountries={unifiedVisibleCountries}
      browseMode={browseMode}
      selectedCountry={selectedCountry}
      highlightedProduct={highlightedProduct}
      onPartnerClick={onPartnerClick}
    />
  );
}

// ─── Onboarding Tab ──────────────────────────────────────────────────────────

function OnboardingTab({
  onboardingSystems,
  onboardingColumnGroups,
  platformProducts,
  COUNTRIES,
  getCountryFlag,
  onOnboardingClick,
}: {
  onboardingSystems: OnboardingSystem[];
  onboardingColumnGroups: ColumnGroups | null;
  platformProducts: Product[];
  COUNTRIES: Country[];
  getCountryFlag: (countryName: string) => string;
  onOnboardingClick: (data: OnboardingCountry) => void;
}) {
  if (onboardingSystems.length === 0 || !onboardingColumnGroups) {
    return <EmptyState icon={Building2} message="No onboarding data available" />;
  }

  const systemsByCountry = onboardingSystems.reduce((acc, s) => {
    if (!acc[s.country]) acc[s.country] = [];
    acc[s.country].push(s);
    return acc;
  }, {} as Record<string, OnboardingSystem[]>);

  const countriesWithProducts = new Set<string>();
  platformProducts
    .filter(p => String(p.solution_level) === '1' && p.category === 'Solution')
    .forEach(p => {
      Object.entries(p.countries).forEach(([code, available]) => {
        if (available === true) {
          countriesWithProducts.add(code);
          const info = COUNTRIES.find(c => c.code === code);
          if (info) countriesWithProducts.add(info.name);
        }
      });
    });

  const allColumnKeys = [
    ...onboardingColumnGroups.boardingSystems,
    ...onboardingColumnGroups.underwritingSystems,
    ...onboardingColumnGroups.merchantPortal,
    ...onboardingColumnGroups.partnerPortal,
    ...onboardingColumnGroups.chargebackManagement,
    ...onboardingColumnGroups.monthlyStatements,
    ...onboardingColumnGroups.liveTransactions,
  ].map(col => col.key);

  const filtered = Object.entries(systemsByCountry).filter(([country, systems]) => {
    if (!countriesWithProducts.has(country)) return false;
    const s = systems[0];
    if (!s) return false;
    return allColumnKeys.some(key => (s as unknown as Record<string, string>)[key] === 'Y');
  });

  if (filtered.length === 0) {
    return <EmptyState icon={Building2} message="No onboarding data available" />;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {filtered.map(([country, systems]) => {
        const info = COUNTRIES.find(c => c.code === country || c.name === country);
        const flag = info?.flag || getCountryFlag(country);
        const name = info?.name || country;
        return (
          <div
            key={country}
            onClick={() => onOnboardingClick({ country, countryName: name, countryFlag: flag, systems })}
            className="bg-transparent border border-border rounded-lg p-6 shadow-sm cursor-pointer hover:shadow-md hover:border-blue-400 transition-all group"
          >
            <div className="flex flex-col items-center justify-center text-center gap-3">
              <span className="text-5xl">{flag}</span>
              <h3 className="text-lg font-semibold text-foreground group-hover:text-blue-600 transition-colors">{name}</h3>
              <p className="text-xs text-muted-foreground">Click for details</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Dispatcher ─────────────────────────────────────────────────────────

export function TabContent({
  activeTab,
  tabSearchQuery,
  selectedPlatform,
  tabs,
  browseMode,
  selectedCountry,
  COUNTRIES,
  unifiedVisibleCountries,
  searchVisibleCountries,
  useCompactMode,
  hardwareProducts,
  softwareProducts,
  lbm2Products,
  level2Platforms,
  level2SearchResults,
  platformProducts,
  onboardingSystems,
  onboardingColumnGroups,
  highlightedProduct,
  getCountryFlag,
  onDeviceClick,
  onProductClick,
  onPartnerClick,
  onOnboardingClick,
}: TabContentProps) {
  if (tabSearchQuery.trim() && selectedPlatform !== 'Integrated Payments') {
    return (
      <SearchResultsSection
        hardwareProducts={hardwareProducts}
        softwareProducts={softwareProducts}
        level2SearchResults={level2SearchResults}
        visibleCountries={searchVisibleCountries}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        highlightedProduct={highlightedProduct}
        tabs={tabs}
        tabSearchQuery={tabSearchQuery}
        onDeviceClick={onDeviceClick}
        onProductClick={onProductClick}
      />
    );
  }

  if (activeTab === 'supported-partners') {
    return (
      <SupportedPartnersTab
        platformProducts={platformProducts}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        COUNTRIES={COUNTRIES}
        unifiedVisibleCountries={unifiedVisibleCountries}
        onPartnerClick={onPartnerClick}
      />
    );
  }

  if (activeTab === 'supported-hardware') {
    return hardwareProducts.length > 0 ? (
      <HardwareTableSection
        products={hardwareProducts}
        visibleCountries={unifiedVisibleCountries}
        includeHeader={useCompactMode}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        onDeviceClick={onDeviceClick}
        countries={COUNTRIES}
      />
    ) : (
      <EmptyState icon={Monitor} message="Currently there is nothing to show here" />
    );
  }

  if (activeTab === 'level2-payments') {
    if (!level2Platforms['Shift4 Payments']) {
      return <EmptyState icon={Package} message="Currently there is nothing to show here" />;
    }
    const paymentsData =
      selectedPlatform === 'Shift4 Payments'
        ? {
            ...level2Platforms['Shift4 Payments'],
            products: level2Platforms['Shift4 Payments'].products.filter(
              p => p.hardware_family?.trim().toUpperCase() !== 'PAX'
            ),
          }
        : level2Platforms['Shift4 Payments'];
    return (
      <Level2PlatformTableSection
        platformName="Shift4 Payments"
        platformData={paymentsData}
        visibleCountries={COUNTRIES}
        includeHeader={false}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        selectedPlatform={selectedPlatform}
        onProductClick={onProductClick}
        countries={COUNTRIES}
        highlightedProduct={highlightedProduct}
      />
    );
  }

  if (activeTab === 'supported-payments') {
    if (selectedPlatform === 'Shift4 Dine') return <DinePaymentsTable />;
    if (selectedPlatform === 'Shift4 One') return <DinePaymentsTable endpoint="/api/one-payments" />;
    if (selectedPlatform === 'Shift4 Duo') return <DinePaymentsTable endpoint="/api/duo-payments" />;

    if (!level2Platforms['Shift4 Payments']) {
      return <EmptyState icon={CreditCard} message="No payment methods available" />;
    }
    return (
      <Level2PlatformTableSection
        platformName="Supported Payments"
        platformData={{
          platform_description: 'Payment methods supported by this platform',
          products: level2Platforms['Shift4 Payments'].products.filter(
            p => p.category !== 'Local Payment Methods' && p.hardware_family?.trim().toUpperCase() !== 'PAX'
          ),
        }}
        visibleCountries={unifiedVisibleCountries}
        includeHeader={false}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        selectedPlatform={selectedPlatform}
        onProductClick={onProductClick}
        countries={COUNTRIES}
        highlightedProduct={highlightedProduct}
      />
    );
  }

  if (activeTab === 'card-payments') {
    const cardProducts = platformProducts.filter(
      p => p.category === 'Payment Card' || p.category === 'Wallets'
    );
    return cardProducts.length > 0 ? (
      <SoftwareTableSection
        products={cardProducts}
        visibleCountries={COUNTRIES}
        includeHeader={useCompactMode}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        onProductClick={onProductClick}
        countries={COUNTRIES}
        highlightedProduct={highlightedProduct}
      />
    ) : (
      <EmptyState icon={CreditCard} message="No card payment methods available" />
    );
  }

  if (activeTab === 'other-payment-methods') {
    const otherProducts = platformProducts.filter(
      p => p.platform === 'Payment Methods' && p.category !== 'Payment Card' && p.category !== 'Wallets'
    );
    return otherProducts.length > 0 ? (
      <SoftwareTableSection
        products={otherProducts}
        visibleCountries={COUNTRIES}
        includeHeader={useCompactMode}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        onProductClick={onProductClick}
        countries={COUNTRIES}
        highlightedProduct={highlightedProduct}
      />
    ) : (
      <EmptyState icon={Package} message="No other payment methods available" />
    );
  }

  if (activeTab === 'product-modules') {
    const moduleProducts =
      selectedPlatform === 'eCommerce'
        ? platformProducts.filter(p => p.category !== 'Hardware' && p.platform === 'eCommerce')
        : softwareProducts;
    const moduleCountries =
      selectedPlatform === 'eCommerce'
        ? getVisibleCountries(moduleProducts, COUNTRIES)
        : unifiedVisibleCountries;
    return moduleProducts.length > 0 ? (
      <SoftwareTableSection
        products={moduleProducts}
        visibleCountries={moduleCountries}
        includeHeader={useCompactMode}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        onProductClick={onProductClick}
        countries={COUNTRIES}
        highlightedProduct={highlightedProduct}
      />
    ) : (
      <EmptyState icon={Settings} message="Currently there is nothing to show here" />
    );
  }

  if (activeTab === 'level3-lbm-2.0') {
    return lbm2Products.length > 0 ? (
      <Level2PlatformTableSection
        platformName="Shift4 Business Manager"
        platformData={{
          platform_description:
            'Advanced version of Lighthouse Business Manager with enhanced tools for restaurants.',
          products: lbm2Products,
        }}
        visibleCountries={unifiedVisibleCountries}
        includeHeader={false}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        selectedPlatform={selectedPlatform}
        onProductClick={onProductClick}
        countries={COUNTRIES}
        highlightedProduct={highlightedProduct}
      />
    ) : (
      <EmptyState icon={Building2} message="No Shift4 Business Manager products available" />
    );
  }

  if (activeTab.startsWith('vertical-') && selectedPlatform === 'Integrated Payments') {
    return (
      <IntegratedPaymentsVerticalTab
        activeTab={activeTab}
        tabSearchQuery={tabSearchQuery}
        platformProducts={platformProducts}
        unifiedVisibleCountries={unifiedVisibleCountries}
        browseMode={browseMode}
        selectedCountry={selectedCountry}
        highlightedProduct={highlightedProduct}
        onPartnerClick={onPartnerClick}
      />
    );
  }

  if (activeTab === 'onboarding') {
    return (
      <OnboardingTab
        onboardingSystems={onboardingSystems}
        onboardingColumnGroups={onboardingColumnGroups}
        platformProducts={platformProducts}
        COUNTRIES={COUNTRIES}
        getCountryFlag={getCountryFlag}
        onOnboardingClick={onOnboardingClick}
      />
    );
  }

  // Other Level 2 platform tabs
  const platformKey = Object.keys(level2Platforms).find(
    key => `level2-${key.toLowerCase().replace(/\s+/g, '-')}` === activeTab
  );
  return platformKey && level2Platforms[platformKey] ? (
    <Level2PlatformTableSection
      platformName={platformKey}
      platformData={level2Platforms[platformKey]}
      visibleCountries={unifiedVisibleCountries}
      includeHeader={false}
      browseMode={browseMode}
      selectedCountry={selectedCountry}
      selectedPlatform={selectedPlatform}
      onProductClick={onProductClick}
      countries={COUNTRIES}
      highlightedProduct={highlightedProduct}
    />
  ) : (
    <EmptyState icon={Building2} message="Currently there is nothing to show here" />
  );
}
